/**
 * The enabler catalog — V108-4-1 / ADR-0085 §1 (reviewed code, not content).
 *
 * An enabler is one action the customer can take that raises what GAC Pro can
 * see. The catalog is the closed set of codes: plain words, who acts, effort,
 * privilege, the ONE `verified_by` rule the server evaluates (never a
 * checkbox), the per-renderer instructions and what each code unlocks.
 * Codes, words, effort and rules change by PR, like `connectors/providers.ts`
 * (D-CI-1). No LLM writes an enabler or its instructions (ADR-0039).
 *
 * Seeded from the connector manifests (one code per creatable kind and per
 * customer-collector kind) plus the CI gate, SBOM push, environment tags,
 * the full / local repo scan and the cloud AI read grants. Built lazily from
 * the live registry so the `@aigovops/core/testing` manifest override is seen.
 *
 * Instructions never carry a secret or a token value: they name secrets
 * (`GACPRO_TOKEN`) only, and a test scans every rendered instruction for
 * credential patterns. The CLI command is built from the published trust
 * config (`release/gacpro-trust.json`) and always names an exact version;
 * while it has no CLI version yet there is no command (`version_pinned:
 * false`, first step "Open Setup in GAC Pro."), never an unpinned npx and
 * never a placeholder (V108-5-5 H-2).
 *
 * Pure data + pure functions; no runtime imports (check-imports).
 */
import { CONNECTOR_MANIFESTS, creatableKinds, kindsManagedBy } from '../connectors/manifests.js';
export const ENABLER_ACTORS = ['repo_agent', 'admin_console', 'collector'];
export const VERIFIED_BY_KINDS = [
    'connector_check_passed', 'ci_submission_received', 'bom_received', 'collector_push_received',
    'tag_observed', 'scan_mode_enabled', 'scan_results_received', 'couldnt_check_cleared', 'evidence_linked', 'connector_active',
    'system_in_program', 'repos_selected', 'account_read', 'system_has_obligations',
];
const CODE_RE = /^[a-z][a-z0-9-]*(:[a-z0-9][a-z0-9_-]*)?$/;
export function isEnablerCode(v) {
    return typeof v === 'string' && v.length <= 80 && CODE_RE.test(v);
}
// ---- effort (minutes) ---------------------------------------------------------------------------------------
/** Effort for a connect step by the manifest's primary auth kind (rough, reviewed; ranks only). */
const CONNECT_EFFORT = {
    oauth: 5, azure_multitenant: 10, api_token: 10, basic_api_key: 10, pat: 10, oauth2: 15,
    aws_oidc: 20, aws_role: 20, gcp_wif: 20, gcp_sa_key: 20, collector_key: 30, collector_token: 30, webhook: 15, none: 10,
};
function primaryAuthKind(m) {
    return (m.auth.find((a) => !a.weaker) ?? m.auth[0])?.kind ?? 'none';
}
function privilegeOf(m) {
    return m.privilege ? { level: 'beyond_read_only', reason: m.privilege.reason } : { level: 'read_only' };
}
// ---- seeded codes -------------------------------------------------------------------------------------------
/** Kinds that discover AI apps from IdP sign-in grants: their code is an IdP grant, not a posture connection. */
const IDP_GRANT_KINDS = { okta: 'grant:okta-app-discovery', entra: 'grant:entra-app-discovery' };
/** The code that activates a creatable connector kind. */
export function connectCodeFor(kind) {
    return IDP_GRANT_KINDS[kind] ?? `connect:${kind}`;
}
function connectEntry(m) {
    const grant = IDP_GRANT_KINDS[m.kind];
    const privilege = privilegeOf(m);
    const steps = [
        `In GAC Pro, open Setup and find ${m.ui.label} under ${m.ui.group}.`,
        grant
            ? `Approve read-only access to sign-in grants in ${m.ui.label} (an administrator of your identity provider does this).`
            : `Follow the connect form. GAC Pro asks for read-only access${privilege.level === 'beyond_read_only' ? ', except where the form explains why the vendor needs more' : ''}.`,
        'GAC Pro runs a first check right away; this step is done when it succeeds.',
    ];
    return {
        code: connectCodeFor(m.kind),
        title: grant ? `Let GAC Pro see AI apps people sign into through ${m.ui.label.split(' ·')[0]}` : `Connect ${m.ui.label}`,
        why: grant
            ? 'Shows which AI apps your people use, from sign-in grants, without reading any content.'
            : m.outputs.checks.length
                ? `Checks ${m.outputs.checks.length === 1 ? 'one setting' : `${m.outputs.checks.length} settings`} in ${m.ui.label} automatically, every day.`
                : `Adds what ${m.ui.label} runs to your inventory automatically.`,
        actor: 'admin_console',
        effort_min: CONNECT_EFFORT[primaryAuthKind(m)] ?? 15,
        privilege,
        subject: 'connector_kind',
        verified_by: { kind: 'connector_check_passed', connector_kind: m.kind },
        steps,
        secret_names: [],
        unlocks: { connector_kinds: [m.kind], checks: [...m.outputs.checks], blueprint_slots: [...(m.blueprint?.slots ?? [])] },
        prerequisites: [],
    };
}
function collectorEntry(m) {
    return {
        code: `collector:${m.kind}`,
        title: `Run the GAC Pro collector for ${m.ui.label}`,
        why: `Reads ${m.ui.label} from inside your network, so nothing private is exposed to the internet.`,
        actor: 'collector',
        effort_min: 30,
        privilege: privilegeOf(m),
        subject: 'connector_kind',
        verified_by: { kind: 'collector_push_received', connector_kind: m.kind },
        steps: [
            'In GAC Pro, open Setup and register a collector. Copy the one-time registration code.',
            'Install the collector with Helm in the cluster or network that can reach the service, passing the registration code.',
            `The collector reads ${m.ui.label} with read-only access and pushes signed results; this step is done when the first push arrives.`,
        ],
        secret_names: [],
        unlocks: { connector_kinds: [m.kind], checks: [...m.outputs.checks], blueprint_slots: [...(m.blueprint?.slots ?? [])] },
        prerequisites: [],
    };
}
const AWS_AI_READ_CHECKS = ['ai_usage_logging_enabled', 'ai_guardrails_configured', 'ai_prompt_attack_filter', 'ai_rag_sources_governed', 'agent_memory_retention', 'ai_model_inventory', 'ai_model_cards', 'ai_model_monitoring', 'ai_service_authn_required'];
const GCP_AI_READ_CHECKS = ['ai_usage_logging_enabled', 'ai_model_inventory', 'ai_model_monitoring', 'ai_guardrails_configured', 'ai_prompt_attack_filter', 'vector_store_encrypted', 'ai_threat_detection_active'];
/**
 * V108-8-8: the CI check keys a GAC Pro CI gate unlocks, in alias form (`canonicalCheckKey`), so the
 * step counts objectives before its first submission. List ONLY a key that an approved test version
 * carries in `check_aliases`, and only where the gate's verdict is honest evidence for that test.
 * The per-duty `gac-policy-eval` rows are not one (V108-8-10: no aliases, docs/v10.8/ci-evidence-aliases.md).
 * V108-8-11: `gac_policy_gate`, the check the SERVER derives from a verified, trusted, default-branch, non-fork
 * gate run (`/api/ci/systems/:id/checks`, core `ci-policy-gate.ts`); the approved version of
 * `change.governance-gate.default-branch` (migration 630) aliases it. Until that content is approved no test
 * carries the alias, so the step still unlocks nothing (closes() reads approved aliases only). After the first
 * submission the step also unlocks tests its checks were bound to (`control_checks.test_key`, the sbom kind).
 */
export const CI_GATE_CHECKS = ['gac_policy_gate'];
/** Codes that are not one-per-kind (hand-written; reviewed here). */
function fixedEntries() {
    return [
        // V108-8-17 (owner decision 2026-10-18): a system no program counts (often one `gac init` registered). The step
        // offers one "Add to <program>" per program with periods (the existing boundary write); nothing joins by itself.
        {
            code: 'scope:system',
            title: 'Add this system to a program',
            why: 'A system no program counts is in no readiness, scope or report.',
            actor: 'admin_console', effort_min: 2, privilege: { level: 'read_only' }, subject: 'system',
            verified_by: { kind: 'system_in_program' },
            steps: [
                'Choose the program whose scope covers this system.',
                'If it is out of scope for every program, skip this step and say why.',
            ],
            secret_names: [],
            unlocks: { connector_kinds: [], checks: [], blueprint_slots: [] },
            prerequisites: [],
        },
        // V108-8-19 (owner decision 2026-10-18): a system with no obligations (never classified, often one `gac init`
        // registered, or answers that match no rule). Its CI gate runs the signed baseline bundle (no AI-duty checks)
        // until then, so the step is ranked right before that system's CI gate step. The questions open in place.
        {
            code: 'classify:system',
            title: 'Classify this system',
            why: 'Until GAC Pro knows what the system does, its CI gate runs a baseline policy with no AI-duty checks.',
            actor: 'admin_console', effort_min: 3, privilege: { level: 'read_only' }, subject: 'system',
            verified_by: { kind: 'system_has_obligations' },
            steps: [
                'Answer the questions about where the system is used, who it affects, the data it handles and the decisions it supports.',
                'GAC Pro works out its obligations when you save; the next CI run checks them.',
                'If your answers match no rule GAC Pro tracks yet, the CI gate keeps running its baseline policy. Skip this step and say why.',
            ],
            secret_names: [],
            unlocks: { connector_kinds: [], checks: [], blueprint_slots: [] },
            prerequisites: [],
            ranks_before: ['ci-gate:github', 'ci-gate:gitlab'],
        },
        // V108-8-18 (d): a GitHub App installation whose repository pick was dismissed, expired, or that reads none yet.
        {
            code: 'github:repos',
            title: 'Pick the repositories GAC Pro reads',
            why: 'The GAC Pro app is installed on GitHub, but it reads no repository there yet.',
            actor: 'admin_console', effort_min: 3, privilege: { level: 'read_only' }, subject: 'account',
            verified_by: { kind: 'repos_selected' },
            steps: [
                'Pick the repositories GAC Pro may read (it never writes).',
                'If the app can read none yet, add repositories to the installation on GitHub, then pick them here.',
                'If you don\'t want this account connected, skip this step.',
            ],
            secret_names: [],
            unlocks: { connector_kinds: [], checks: [], blueprint_slots: [] },
            prerequisites: [],
        },
        // V108-8-18 (a, b): a Google Cloud project / organization, or an account a program boundary names, that nothing reads.
        {
            code: 'read:account',
            title: 'Connect this account',
            why: 'Nothing reads this account yet, so nothing in it counts.',
            actor: 'admin_console', effort_min: 15, privilege: { level: 'read_only' }, subject: 'account',
            verified_by: { kind: 'account_read' },
            steps: [
                'Connect a read-only reader for this account (Setup → Connect).',
                'If it is not part of what you certify, skip this step and say why.',
            ],
            secret_names: [],
            unlocks: { connector_kinds: [], checks: [], blueprint_slots: [] },
            prerequisites: [],
        },
        {
            code: 'ci-gate:github',
            title: 'Add the GAC Pro check to GitHub Actions',
            why: 'Every pull request is checked against your policies, and the results arrive signed.',
            actor: 'repo_agent', effort_min: 15, privilege: { level: 'read_only' }, subject: 'system',
            verified_by: { kind: 'ci_submission_received', trusted_only: true },
            steps: [
                'In GAC Pro, open Settings → API tokens and create a token for the CI gate purpose for this system.',
                'Save it as the repository secret GACPRO_TOKEN.',
                'Run the command in the repository; it adds the workflow file on a new branch and opens a pull request.',
                'Merge the pull request. After its first signed run on your default branch, an owner or admin trusts the workflow in Setup ("Trust it").',
                'This step is done when a signed run from the trusted workflow arrives.',
            ],
            secret_names: ['GACPRO_TOKEN'],
            unlocks: { connector_kinds: ['sbom'], checks: [...CI_GATE_CHECKS], blueprint_slots: ['ci_cd'] },
            prerequisites: [],
        },
        {
            code: 'ci-gate:gitlab',
            title: 'Add the GAC Pro check to GitLab CI',
            why: 'Every merge request is checked against your policies, and the results arrive signed.',
            actor: 'repo_agent', effort_min: 15, privilege: { level: 'read_only' }, subject: 'system',
            verified_by: { kind: 'ci_submission_received' },
            steps: [
                'In GAC Pro, open Settings → API tokens and create a token for the CI gate purpose for this system.',
                'Save it as the masked CI/CD variable GACPRO_TOKEN.',
                'Run the command in the repository; it adds the job to .gitlab-ci.yml on a new branch for review.',
                'Merge the change; this step is done when the first signed check arrives.',
            ],
            secret_names: ['GACPRO_TOKEN'],
            unlocks: { connector_kinds: ['sbom'], checks: [...CI_GATE_CHECKS], blueprint_slots: ['ci_cd'] },
            prerequisites: [],
        },
        {
            code: 'sbom-push',
            title: 'Send a software bill of materials from CI',
            why: 'Lists every component this system ships, even where a repository scan stopped early.',
            actor: 'repo_agent', effort_min: 10, privilege: { level: 'read_only' }, subject: 'system',
            verified_by: { kind: 'bom_received' },
            steps: [
                'In GAC Pro, create a token for the SBOM push purpose for this system and save it as the CI secret GACPRO_TOKEN.',
                'Run the command in the repository; it adds an SBOM step to the CI workflow on a new branch for review.',
                'Merge the change; this step is done when the first SBOM arrives.',
            ],
            secret_names: ['GACPRO_TOKEN'],
            unlocks: { connector_kinds: ['sbom'], checks: [], blueprint_slots: [] },
            prerequisites: [],
        },
        {
            code: 'tag:environment',
            title: 'Tag cloud resources with their environment',
            why: 'Lets GAC Pro tell production from staging, so only production moves your readiness.',
            actor: 'repo_agent', effort_min: 30, privilege: { level: 'read_only' }, subject: 'connector',
            verified_by: { kind: 'tag_observed', keys: ['environment', 'env', 'stage'] },
            steps: [
                'Add a tag named environment (production, staging or development) to the resources in your infrastructure code.',
                'Run the command in the repository; it proposes the tags on a new branch for review.',
                'Apply the change; this step is done when GAC Pro sees the tag on the next collection.',
            ],
            secret_names: [],
            unlocks: { connector_kinds: [], checks: [], blueprint_slots: [] },
            prerequisites: [],
        },
        {
            code: 'scan:full',
            title: 'Turn on the full repository scan',
            why: 'Finds AI SDKs and outbound services in source code, not only in manifests and infrastructure code.',
            actor: 'admin_console', effort_min: 2, privilege: { level: 'read_only' }, subject: 'repo',
            verified_by: { kind: 'scan_mode_enabled' },
            steps: [
                'In GAC Pro, open Setup and choose the repository under Source control.',
                'Turn on Full scan. An owner or admin can turn it off again at any time.',
            ],
            secret_names: [],
            unlocks: { connector_kinds: [], checks: [], blueprint_slots: [] },
            prerequisites: ['connect:github'],
        },
        {
            code: 'scan:local',
            title: 'Scan the repository locally and upload the results',
            why: 'Covers a repository the automatic scan could not finish, without giving GAC Pro more access.',
            actor: 'repo_agent', effort_min: 10, privilege: { level: 'read_only' }, subject: 'system',
            verified_by: { kind: 'scan_results_received' },
            steps: [
                'In GAC Pro, create a token for the local scan purpose for this system and set it as GACPRO_TOKEN in your shell or CI.',
                'Run the command in the repository; it scans your checkout and uploads only the extracted facts.',
            ],
            secret_names: ['GACPRO_TOKEN'],
            unlocks: { connector_kinds: [], checks: [], blueprint_slots: [] },
            prerequisites: [],
        },
        {
            code: 'grant:aws-ai-read',
            title: 'Let the GAC Pro AWS role read Bedrock and SageMaker',
            why: 'Checks your AI services on AWS (logging, guardrails, model inventory) with read-only access.',
            actor: 'repo_agent', effort_min: 15, privilege: { level: 'read_only' }, subject: 'connector',
            verified_by: { kind: 'connector_check_passed', connector_kind: 'aws', checks: AWS_AI_READ_CHECKS },
            steps: [
                'Attach the AWS managed policies AmazonBedrockReadOnly and AmazonSageMakerReadOnly to the role GAC Pro uses, plus an inline policy allowing cloudwatch:ListMetrics and cloudwatch:GetMetricData (Bedrock metrics, aggregates only).',
                'Run the command in your infrastructure repository; it proposes the change on a new branch for review.',
                'Apply the change; this step is done when the next AWS collection records an AI check.',
            ],
            secret_names: [],
            unlocks: { connector_kinds: [], checks: [...AWS_AI_READ_CHECKS], blueprint_slots: [] },
            prerequisites: ['connect:aws'],
        },
        {
            code: 'grant:gcp-ai-read',
            title: 'Let the GAC Pro Google Cloud identity read Vertex AI',
            why: 'Checks your AI services on Google Cloud (logging, Model Armor, model inventory) with read-only access.',
            actor: 'repo_agent', effort_min: 15, privilege: { level: 'read_only' }, subject: 'connector',
            verified_by: { kind: 'connector_check_passed', connector_kind: 'gcp', checks: GCP_AI_READ_CHECKS },
            steps: [
                'Grant the roles aiplatform.viewer, modelarmor.viewer and monitoring.viewer (Vertex metrics, aggregates only) to the identity GAC Pro uses.',
                'Run the command in your infrastructure repository; it proposes the change on a new branch for review.',
                'Apply the change; this step is done when the next Google Cloud collection records an AI check.',
            ],
            secret_names: [],
            unlocks: { connector_kinds: [], checks: [...GCP_AI_READ_CHECKS], blueprint_slots: [] },
            prerequisites: ['connect:gcp'],
        },
        ...couldntCheckFixEntries(),
    ];
}
/**
 * V109-1-10 (LM-9): the fixes for "Couldn't check" reasons (enablers/couldnt-check.ts names
 * one per reason). Each is done only when a later collection shows the reason gone.
 */
function couldntCheckFixEntries() {
    const none = { connector_kinds: [], checks: [], blueprint_slots: [] };
    const fix = (e) => ({ ...e, privilege: { level: 'read_only' }, secret_names: [], unlocks: none, prerequisites: [] });
    return [
        fix({
            code: 'fix:reconnect', title: 'Reconnect this source with read-only access',
            why: "GAC Pro's access was refused or expired, so some settings couldn't be checked.",
            actor: 'admin_console', effort_min: 10, subject: 'connector',
            verified_by: { kind: 'couldnt_check_cleared', reasons: ['access_denied', 'credential_expired'] },
            steps: [
                'In GAC Pro, open Setup and choose this connection.',
                'Reconnect it, granting the read-only role or permission the connection form names.',
                'GAC Pro collects again right away; this step is done when that collection can read everything.',
            ],
        }),
        fix({
            code: 'fix:enable-api', title: 'Turn on the service API GAC Pro reads',
            why: 'A service API is turned off, so GAC Pro could not read the setting.',
            actor: 'admin_console', effort_min: 10, subject: 'connector',
            verified_by: { kind: 'couldnt_check_cleared', reasons: ['api_not_enabled'] },
            steps: [
                "In the provider's console, turn on the API named in the connection's details for the connected project or account.",
                'In GAC Pro, open the connection and choose Check connection; this step is done when the next collection reads it.',
            ],
        }),
        fix({
            code: 'fix:collect-again', title: 'Collect from this source again',
            why: 'The last collection did not finish, so its results do not count yet.',
            actor: 'admin_console', effort_min: 2, subject: 'connector',
            verified_by: { kind: 'couldnt_check_cleared', reasons: ['read_truncated', 'rate_limited', 'collector_error', 'stale'] },
            steps: [
                'GAC Pro retries on its own at the next scheduled collection.',
                'To retry now, open the connection in GAC Pro and choose Check connection; this step is done when a collection finishes.',
            ],
        }),
        fix({
            code: 'fix:send-measurement', title: 'Send the measured value with each CI check',
            why: 'A tool said pass or fail without the number the policy checks, so it does not count.',
            actor: 'repo_agent', effort_min: 15, subject: 'system',
            verified_by: { kind: 'couldnt_check_cleared', reasons: ['missing_measurement'] },
            steps: [
                'Configure the tool in CI to write its measurements (for example coverage or score) into its report.',
                'Run the pipeline; this step is done when GAC Pro receives a check with the measurement.',
            ],
        }),
        // V109-1-19 R3 (R2-B1): a connection someone turned off is never scheduled; turning it back on is the fix.
        fix({
            code: 'fix:turn-on', title: 'Turn this connection back on',
            why: 'The connection is turned off, so GAC Pro stopped collecting from it.',
            actor: 'admin_console', effort_min: 2, subject: 'connector',
            verified_by: { kind: 'connector_active' },
            steps: [
                'In GAC Pro, open Setup and find this connection under Manage sources.',
                'Turn it back on; GAC Pro collects from it again at once. This step is done when the connection is on.',
            ],
        }),
        // V109-1-19 R3 (R2-H2): one step per control (`control:<program>/<requirement_id>`), opening that
        // control's statement editor on the value.
        fix({
            code: 'fix:set-parameters', title: "Set your organization's values for this control",
            why: 'The control needs a value your organization chooses before it can be checked.',
            actor: 'admin_console', effort_min: 10, subject: 'control',
            verified_by: { kind: 'couldnt_check_cleared', reasons: ['odp_unset'] },
            steps: [
                'Open the control in GAC Pro; its statement editor opens on the value to set.',
                'Fill in the value and save; this step is done when the next check uses it.',
            ],
        }),
        fix({
            code: 'fix:connect-source', title: 'Connect the source that holds this evidence',
            why: 'Nothing connected can read this setting yet.',
            actor: 'admin_console', effort_min: 15, subject: 'workspace',
            verified_by: { kind: 'couldnt_check_cleared', reasons: ['not_connected'] },
            steps: [
                'In GAC Pro, open Setup and connect the service this control is about.',
                'This step is done when its first collection is checked.',
            ],
        }),
        // V109-1-19 R3 (R2-H3): one step per test (`test:<test_key>`); the upload is pre-filled with the test.
        // `mapping_unapproved` (GAC Pro approves the mappings, R2-B2) offers this as "Attach evidence instead"
        // on the row but never gets a Setup step: the review queue is GAC Pro's, not the customer's.
        fix({
            code: 'fix:attach-evidence', title: 'Attach evidence for what GAC Pro cannot check',
            why: 'GAC Pro cannot read this automatically yet; a document or screenshot covers it.',
            actor: 'admin_console', effort_min: 15, subject: 'test',
            verified_by: { kind: 'evidence_linked', reasons: ['unsupported'] },
            steps: [
                'Upload the document or screenshot that shows the setting; the upload is already linked to the test it proves.',
                'It counts from the next check; this step is done when every such result has evidence linked.',
            ],
        }),
    ];
}
let cache = null;
/** The catalog for the live connector registry, sorted by code. */
export function enablerCatalog() {
    if (cache && cache.manifests === CONNECTOR_MANIFESTS)
        return cache.entries;
    const creatable = new Set(creatableKinds());
    const collectors = new Set(kindsManagedBy('customer_collector'));
    const entries = [
        ...CONNECTOR_MANIFESTS.filter((m) => creatable.has(m.kind)).map(connectEntry),
        ...CONNECTOR_MANIFESTS.filter((m) => collectors.has(m.kind)).map(collectorEntry),
        ...fixedEntries(),
    ].sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
    cache = { manifests: CONNECTOR_MANIFESTS, entries: Object.freeze(entries) };
    return cache.entries;
}
export function getEnablerCatalogEntry(code) {
    return enablerCatalog().find((e) => e.code === code);
}
/**
 * ADR-0085 Consequences: every creatable (and customer-collector) connector kind maps to
 * ≥ 1 catalog code. Returns the kinds that don't (empty = valid).
 */
export function kindsWithoutEnabler() {
    const covered = new Set(enablerCatalog().flatMap((e) => e.unlocks.connector_kinds));
    return [...creatableKinds(), ...kindsManagedBy('customer_collector')].filter((k) => !covered.has(k)).sort();
}
// ---- instructions (one list, two renderers: ADR-0085 §7) ---------------------------------------------------
/** The published npm package of the CLI (ADR-0087 §1). */
export const GACPRO_CLI_PACKAGE = '@gacpro/cli';
/**
 * The CLI version from the published trust config (`cli.npm.version`, ADR-0087 §1), or null
 * while the trust config has none (V108-2 adds the section). Accepts the trust object as data
 * so the caller passes `GACPRO_TRUST`.
 */
export function cliVersionFromTrust(trust) {
    const v = trust?.cli?.npm?.version;
    return typeof v === 'string' && /^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/.test(v) ? v : null;
}
/** Shown instead of a command while no exact CLI version is published (V108-5-5 H-2: never an unpinned npx). */
export const OPEN_SETUP_STEP = 'Open Setup in GAC Pro.';
export function renderEnablerInstructions(entry, cliVersion) {
    const agentRun = entry.actor === 'repo_agent';
    // V108-5-5 (H-2): the command always names an exact version. Without one there is no command at
    // all (an unpinned `npx @gacpro/cli` would run whatever `latest` is on that day); Setup takes over.
    const pinned = cliVersion !== null && /^\d+\.\d+\.\d+(-[0-9A-Za-z.-]+)?$/.test(cliVersion);
    return {
        command: agentRun && pinned ? `npx ${GACPRO_CLI_PACKAGE}@${cliVersion} enable ${entry.code}` : null,
        version_pinned: agentRun ? pinned : true,
        steps: agentRun && !pinned ? [OPEN_SETUP_STEP, ...entry.steps] : entry.steps,
        secret_names: entry.secret_names,
    };
}
/** Problems with one catalog entry (empty = valid). The catalog test runs this over every code. */
export function validateEnablerEntry(e) {
    const v = [];
    const at = (m) => v.push(`${e.code}: ${m}`);
    if (!isEnablerCode(e.code))
        at('code must be lower-case `family` or `family:subject`');
    if (!e.title.trim() || e.title.length > 90)
        at('title must be plain words (1..90 characters)');
    if (!e.why.trim() || e.why.length > 140)
        at('why must be one line (1..140 characters)');
    if (!ENABLER_ACTORS.includes(e.actor))
        at(`unknown actor ${String(e.actor)}`);
    if (!(Number.isInteger(e.effort_min) && e.effort_min > 0 && e.effort_min <= 240))
        at('effort_min must be 1..240');
    if (!e.steps.length || e.steps.some((s) => !s.trim()))
        at('needs at least one plain step (the action)');
    if (!e.verified_by || !VERIFIED_BY_KINDS.includes(e.verified_by.kind))
        at('needs exactly one verified_by rule from the closed set');
    if (e.privilege.level === 'beyond_read_only' && !e.privilege.reason.trim())
        at('beyond_read_only needs the manifest reason');
    for (const s of e.secret_names)
        if (!/^[A-Z][A-Z0-9_]+$/.test(s))
            at(`secret name ${s} must be an env-style NAME`);
    return v;
}
//# sourceMappingURL=catalog.js.map